import React, { useState, useMemo } from 'react';
import { clsx } from 'clsx';
import { isToday, isTomorrow, isThisWeek, isPast, startOfDay, endOfDay } from 'date-fns';
import {
  SunIcon,
  CalendarIcon,
  ClockIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { Task, TaskStatus } from '../../../types/task.types';
import { TaskCard, TaskProgressRing } from '../components';
import { Skeleton } from '../../ui';

interface MyTasksViewProps {
  tasks: Task[];
  loading: boolean;
  onTaskClick: (task: Task) => void;
  onTaskComplete: (taskId: number) => void;
  onRefresh: () => void;
}

interface TaskGroup {
  id: string;
  title: string;
  icon: React.ReactNode;
  tasks: Task[];
  color: string;
  isCollapsible?: boolean;
}

const MyTasksView: React.FC<MyTasksViewProps> = ({
  tasks,
  loading,
  onTaskClick,
  onTaskComplete,
  onRefresh,
}) => {
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set(['completed']));

  const toggleGroup = (groupId: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  const taskGroups = useMemo(() => {
    const now = new Date();
    const groups: TaskGroup[] = [];

    // Overdue tasks
    const overdue = tasks.filter(t => 
      t.dueDateTime && 
      isPast(new Date(t.dueDateTime)) && 
      t.status !== TaskStatus.COMPLETED &&
      !isToday(new Date(t.dueDateTime))
    );
    if (overdue.length > 0) {
      groups.push({
        id: 'overdue',
        title: 'Overdue',
        icon: <ClockIcon className="h-5 w-5" />,
        tasks: overdue.sort((a, b) => new Date(a.dueDateTime!).getTime() - new Date(b.dueDateTime!).getTime()),
        color: 'red',
      });
    }

    // Today's tasks
    const today = tasks.filter(t => 
      t.dueDateTime && 
      isToday(new Date(t.dueDateTime)) && 
      t.status !== TaskStatus.COMPLETED
    );
    groups.push({
      id: 'today',
      title: 'Today',
      icon: <SunIcon className="h-5 w-5" />,
      tasks: today.sort((a, b) => new Date(a.dueDateTime!).getTime() - new Date(b.dueDateTime!).getTime()),
      color: 'yellow',
    });

    // Tomorrow's tasks
    const tomorrow = tasks.filter(t => 
      t.dueDateTime && 
      isTomorrow(new Date(t.dueDateTime)) && 
      t.status !== TaskStatus.COMPLETED
    );
    if (tomorrow.length > 0) {
      groups.push({
        id: 'tomorrow',
        title: 'Tomorrow',
        icon: <CalendarIcon className="h-5 w-5" />,
        tasks: tomorrow.sort((a, b) => new Date(a.dueDateTime!).getTime() - new Date(b.dueDateTime!).getTime()),
        color: 'blue',
      });
    }

    // This week (excluding today and tomorrow)
    const thisWeek = tasks.filter(t => {
      if (!t.dueDateTime || t.status === TaskStatus.COMPLETED) return false;
      const due = new Date(t.dueDateTime);
      return isThisWeek(due) && !isToday(due) && !isTomorrow(due) && !isPast(due);
    });
    if (thisWeek.length > 0) {
      groups.push({
        id: 'this-week',
        title: 'This Week',
        icon: <CalendarIcon className="h-5 w-5" />,
        tasks: thisWeek.sort((a, b) => new Date(a.dueDateTime!).getTime() - new Date(b.dueDateTime!).getTime()),
        color: 'purple',
      });
    }

    // Later / No due date
    const later = tasks.filter(t => {
      if (t.status === TaskStatus.COMPLETED) return false;
      if (!t.dueDateTime) return true;
      const due = new Date(t.dueDateTime);
      return !isThisWeek(due) && !isPast(due);
    });
    if (later.length > 0) {
      groups.push({
        id: 'later',
        title: 'Later',
        icon: <SparklesIcon className="h-5 w-5" />,
        tasks: later,
        color: 'gray',
        isCollapsible: true,
      });
    }

    // Completed today
    const completedToday = tasks.filter(t => {
      if (t.status !== TaskStatus.COMPLETED || !t.completedAt) return false;
      return isToday(new Date(t.completedAt));
    });
    if (completedToday.length > 0) {
      groups.push({
        id: 'completed',
        title: 'Completed Today',
        icon: <CheckCircleIcon className="h-5 w-5" />,
        tasks: completedToday,
        color: 'green',
        isCollapsible: true,
      });
    }

    return groups;
  }, [tasks]);

  // Calculate daily progress
  const todayTotal = taskGroups.find(g => g.id === 'today')?.tasks.length || 0;
  const completedToday = taskGroups.find(g => g.id === 'completed')?.tasks.length || 0;
  const dailyGoal = todayTotal + completedToday;

  if (loading) {
    return <LoadingSkeleton />;
  }

  return (
    <div className="space-y-6">
      {/* Progress Header */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Today's Progress</h2>
            <p className="text-sm text-gray-500">
              {completedToday} of {dailyGoal} tasks completed
            </p>
          </div>
          <TaskProgressRing completed={completedToday} total={dailyGoal || 1} size="lg" />
        </div>
      </div>
      {/* Task Groups */}
      {taskGroups.map((group) => {
        const isCollapsed = collapsedGroups.has(group.id);
        const colorClasses = getGroupColorClasses(group.color);

        return (
          <div key={group.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            {/* Group Header */}
            <button
              onClick={() => group.isCollapsible && toggleGroup(group.id)}
              className={clsx(
                'w-full flex items-center justify-between p-4 transition-colors',
                group.isCollapsible && 'hover:bg-gray-50 cursor-pointer',
                !group.isCollapsible && 'cursor-default'
              )}
            >
              <div className="flex items-center gap-3">
                <div className={clsx('p-2 rounded-lg', colorClasses.iconBg)}>
                  <span className={colorClasses.iconText}>{group.icon}</span>
                </div>
                <div className="text-left">
                  <h3 className="font-semibold text-gray-900">{group.title}</h3>
                  <p className="text-sm text-gray-500">{group.tasks.length} task{group.tasks.length !== 1 ? 's' : ''}</p>
                </div>
              </div>
              {group.isCollapsible && (
                <div className="text-gray-400">
                  {isCollapsed ? (
                    <ChevronRightIcon className="h-5 w-5" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5" />
                  )}
                </div>
              )}
            </button>

            {/* Group Content */}
            {!isCollapsed && (
              <div className="px-4 pb-4 space-y-3">
                {group.tasks.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    <CheckCircleIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No tasks here!</p>
                  </div>
                ) : (
                  group.tasks.map((task) => (
                    <TaskCard
                      key={task.id}
                      task={task}
                      onClick={() => onTaskClick(task)}
                      onComplete={() => onTaskComplete(task.id)}
                      variant="default"
                    />
                  ))
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Empty State */}
      {taskGroups.length === 0 && (
        <div className="bg-white rounded-2xl p-12 text-center shadow-sm border border-gray-100">
          <SparklesIcon className="h-16 w-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">All caught up!</h3>
          <p className="text-gray-500">You have no tasks assigned. Enjoy your day!</p>
        </div>
      )}
    </div>
  );
};

const getGroupColorClasses = (color: string) => {
  const colors: Record<string, { iconBg: string; iconText: string }> = {
    red: { iconBg: 'bg-red-100', iconText: 'text-red-600' },
    yellow: { iconBg: 'bg-yellow-100', iconText: 'text-yellow-600' },
    blue: { iconBg: 'bg-blue-100', iconText: 'text-blue-600' },
    purple: { iconBg: 'bg-purple-100', iconText: 'text-purple-600' },
    green: { iconBg: 'bg-green-100', iconText: 'text-green-600' },
    gray: { iconBg: 'bg-gray-100', iconText: 'text-gray-600' },
  };
  return colors[color] || colors.gray;
};

const LoadingSkeleton = () => (
  <div className="space-y-6">
    <Skeleton className="h-32 rounded-2xl" />
    <Skeleton className="h-64 rounded-2xl" />
    <Skeleton className="h-48 rounded-2xl" />
  </div>
);

export default MyTasksView;

